import { useNavigate } from "react-router-dom";
import { useConfig } from "@/hooks/useConfig";
import { Header, Footer } from "@/components/site";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { money, imgUrl, telHref, smsHref } from "@/lib/api";
import { Phone, MessageSquare, Star, MapPin, Navigation, Sun, ShieldCheck, Gauge, Clock, Check, Hexagon, Atom, Flame, Layers, Radio, Snowflake, Award, Waves, Download } from "lucide-react";

const Label = ({ children }) => (
  <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary">{children}</span>
);

const TINT_IMG = "https://static.prod-images.emergentagent.com/jobs/9de85f3d-b29d-402a-981c-8eeb5572c9a8/images/2b686cf05b818d0c0091f66ccb8cbb7fac5886e25d92005d8443ac0a2093a79a.jpeg";
const FLYER_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_fresno-tint-book/artifacts/ktkcx0d6_C492FCD3-2E5C-49A9-80AE-3EBD8BEBC98B.png";

const BENEFITS = [
  { icon: Hexagon, text: "Nano-tech film made from clear polyester" },
  { icon: Atom, text: "Highest-grade ceramic particles" },
  { icon: Flame, text: "Excellent wet/dry heat-shrink ability" },
  { icon: Layers, text: "Scratch-resistant, standard black color" },
  { icon: Sun, text: "Blocks 99% of harmful UV rays" },
  { icon: Radio, text: "No radio or GPS signal interference" },
  { icon: Snowflake, text: "Cooler temperatures & interior protection" },
  { icon: Award, text: "Backed by a lifetime warranty" },
];

function TintCard({ value }) {
  const opacity = Math.min(0.9, (100 - value) / 100);
  return (
    <div className="shrink-0 w-36 sm:w-full">
      <div className="relative overflow-hidden rounded-lg border border-slate-200 aspect-[4/3] shadow-sm">
        <img src={TINT_IMG} alt={`${value}% tint`} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950" style={{ opacity }} />
        <div className="absolute bottom-2 left-2 font-head font-black text-lg text-white drop-shadow">{value}%</div>
      </div>
      <p className="mt-2 text-xs text-center text-slate-500">{value <= 15 ? "Very dark" : value <= 35 ? "Dark" : value <= 50 ? "Medium" : "Light"}</p>
    </div>
  );
}

export default function Landing() {
  const { data, isLoading } = useConfig();
  const navigate = useNavigate();
  if (isLoading || !data) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  const { settings, services, percentages, promotions, gallery, reviews, vehicle_types } = data;
  const phone = settings?.phone || "";
  const sms = settings?.sms_number || phone;
  const priceFor = (s, v) => {
    const base = s.base_price ?? (s.prices ? s.prices[v.id] : 0) ?? 0;
    const amt = v.price_adjustment_amount || 0;
    return Math.round((base + amt) * 100) / 100;
  };

  return (
    <div className="bg-white text-slate-900">
      <Header settings={settings} />

      {/* HERO — split, bright */}
      <section className="pt-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-0 items-stretch">
          <div className="px-5 sm:px-8 py-16 lg:py-24 flex flex-col justify-center fade-up">
            <Label>Fresno, California</Label>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-head font-black uppercase tracking-tighter leading-[0.95]">
              Premium Window Tint,<br /><span className="text-primary">Precision Installed.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl">{settings.about}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-book-btn" onClick={() => navigate("/book")} className="rounded-md h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider">Book an Appointment</Button>
              <a href={telHref(phone)}><Button data-testid="hero-call-btn" variant="outline" className="rounded-md h-12 px-6 border-slate-300"><Phone className="w-4 h-4 mr-2" /> Call</Button></a>
              <a href={smsHref(sms)}><Button data-testid="hero-text-btn" variant="outline" className="rounded-md h-12 px-6 border-slate-300"><MessageSquare className="w-4 h-4 mr-2" /> Text</Button></a>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck className="w-4 h-4 text-primary" /> Every eligible install backed by a 5-Year Warranty
            </div>
          </div>
          <div className="relative min-h-[320px] lg:min-h-full">
            <img src={imgUrl(settings.hero_image)} alt="Professionally tinted car" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[{ icon: Sun, t: "Heat & UV Rejection", d: "Stay cool in the Valley sun" },
            { icon: ShieldCheck, t: "No-Fade Guarantee", d: "Films that never turn purple" },
            { icon: Gauge, t: "Same-Day Service", d: "Fast, precise installs" }].map((f, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-11 h-11 grid place-items-center rounded-md bg-primary/10 text-primary shrink-0"><f.icon className="w-5 h-5" /></div>
              <div><p className="font-head font-bold uppercase text-sm">{f.t}</p><p className="text-sm text-slate-500">{f.d}</p></div>
            </div>
          ))}
        </div>
      </div>

      {promotions?.length > 0 && (
        <div className="bg-primary text-primary-foreground">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 justify-center text-sm font-semibold">
            {promotions.map((p) => <span key={p.id} data-testid="promo-item">★ {p.title} — {p.description}</span>)}
          </div>
        </div>
      )}

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <Label>What We Offer</Label>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-head font-black uppercase tracking-tighter">Tint Services</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.filter((s) => s.available).map((s) => (
            <div key={s.id} data-testid={`service-card-${s.id}`} className={`group rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow ${!s.available ? "opacity-70" : ""}`}>
              <div className="relative aspect-[16/11] overflow-hidden">
                <img src={imgUrl(s.image_url)} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {s.available
                  ? <Badge className="absolute top-3 right-3 rounded-md bg-primary text-primary-foreground">Available</Badge>
                  : <Badge data-testid={`service-unavailable-${s.id}`} className="absolute top-3 right-3 rounded-md bg-destructive text-white">Currently Not Available</Badge>}
              </div>
              <div className="p-5">
                <h3 className="font-head font-bold text-lg uppercase tracking-tight">{s.name}</h3>
                <p className="mt-2 text-sm text-slate-500 min-h-[60px]">{s.description}</p>
                {s.duration && <p className="mt-2 text-xs text-primary uppercase tracking-wide flex items-center gap-1"><Clock className="w-3 h-3" />{s.duration}</p>}
                <div className="mt-3 flex items-baseline gap-2">
                  {!s.price_display && <span className="text-xs text-slate-400 uppercase tracking-wide">From</span>}
                  <span className="font-head font-black text-2xl text-primary">{s.price_display || money(s.base_price ?? 0)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TINT PERCENTAGES */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Label>Choose Your Shade</Label>
          <h2 className="mt-3 text-3xl sm:text-4xl font-head font-black uppercase tracking-tighter">Tint Darkness Guide</h2>
          <p className="mt-3 text-sm text-slate-500 max-w-2xl">See how each shade looks on real glass. A lower percentage lets in less light (darker); a higher percentage is lighter. Grayed-out shades are temporarily unavailable.</p>
          <div className="mt-6 flex items-center justify-between text-xs uppercase tracking-widest text-slate-400 font-bold">
            <span>← Lighter</span><span>Darker →</span>
          </div>
          <div className="mt-3 flex gap-4 overflow-x-auto hide-scrollbar sm:grid sm:grid-cols-3 lg:grid-cols-6 pb-2">
            {percentages.map((p) => (
              <div key={p.id} data-testid={`pct-swatch-${p.value}`} className={!p.available ? "opacity-40 grayscale" : ""}>
                <TintCard value={p.value} />
                {!p.available && <p className="text-xs text-center uppercase text-destructive mt-1">Not Available</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FILM TECHNOLOGY & BENEFITS */}
      <section id="technology" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <Label>Film Technology</Label>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-head font-black uppercase tracking-tighter">Why Our Film Performs</h2>
        <p className="mt-3 text-base text-slate-600 max-w-2xl">Engineered nano-ceramic film built for the Central Valley sun — maximum heat and UV rejection with crystal-clear visibility and full signal clarity.</p>

        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-x-8 gap-y-6">
            {BENEFITS.map((b, i) => (
              <div key={i} data-testid={`benefit-${i}`} className="flex items-start gap-4">
                <div className="w-11 h-11 grid place-items-center rounded-md bg-primary/10 text-primary shrink-0"><b.icon className="w-5 h-5" /></div>
                <p className="text-sm sm:text-base text-slate-700 font-medium pt-2">{b.text}</p>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="performance-card">
            <div className="bg-primary text-primary-foreground px-6 py-5">
              <p className="font-head font-black uppercase tracking-wide text-lg">Performance</p>
              <p className="text-xs uppercase tracking-widest opacity-80">Lab-tested rejection</p>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 grid place-items-center rounded-lg bg-amber-100 text-amber-600 shrink-0"><Sun className="w-6 h-6" /></div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">TSER Rejected</p>
                  <p className="font-head font-black text-2xl text-slate-900" data-testid="tser-stat">43–64%</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 grid place-items-center rounded-lg bg-primary/10 text-primary shrink-0"><Waves className="w-6 h-6" /></div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">IR Rejected (Wide Spectrum)</p>
                  <p className="font-head font-black text-2xl text-slate-900" data-testid="ir-stat">79–89%</p>
                </div>
              </div>
              <a href={FLYER_URL} target="_blank" rel="noreferrer" download className="block">
                <Button data-testid="view-flyer-btn" variant="outline" className="w-full rounded-md border-slate-300 h-11"><Download className="w-4 h-4 mr-2" />View / Download Flyer</Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <Label>Transparent Pricing</Label>
        <h2 className="mt-3 text-3xl sm:text-4xl font-head font-black uppercase tracking-tighter">Pricing</h2>
        <div className="mt-10 overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full text-left min-w-[520px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-4 text-xs uppercase tracking-[0.15em] text-slate-500">Service</th>
                {vehicle_types.map((v) => <th key={v.id} className="p-4 text-xs uppercase tracking-[0.15em] text-slate-500">{v.name}</th>)}
                <th className="p-4 text-xs uppercase tracking-[0.15em] text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {services.filter((s) => s.available).map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-0">
                  <td className="p-4 font-head font-bold uppercase text-sm">{s.name}</td>
                  {s.price_display
                    ? <td colSpan={vehicle_types.length} className="p-4 text-primary font-semibold">{s.price_display}</td>
                    : vehicle_types.map((v) => <td key={v.id} className="p-4 text-primary font-semibold">{money(priceFor(s, v))}{v.price_adjustment_amount ? <span className="text-xs text-slate-400 ml-1">(+{money(v.price_adjustment_amount)})</span> : null}</td>)}
                  <td className="p-4">{s.available ? <span className="text-xs uppercase tracking-wide text-emerald-600">Available</span> : <span className="text-xs uppercase tracking-wide text-destructive">Unavailable</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm text-slate-600">
          <div className="rounded-xl border border-slate-200 p-5 bg-white shadow-sm"><p className="text-slate-900 font-head font-bold uppercase text-sm mb-2">Mobile Service</p><p>We come to you for a {money(settings.mobile_fee)} mobile service fee.</p></div>
          <div className="rounded-xl border border-slate-200 p-5 bg-white shadow-sm"><p className="text-slate-900 font-head font-bold uppercase text-sm mb-2">Payment</p><p>{settings.payment_info?.cash_note}</p><p>{settings.payment_info?.card_note}</p></div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Label>Our Work</Label>
          <h2 className="mt-3 text-3xl sm:text-4xl font-head font-black uppercase tracking-tighter">Gallery & Before / After</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.map((g) => (
              <div key={g.id} data-testid={`gallery-item-${g.id}`} className="relative group overflow-hidden rounded-xl border border-slate-200 aspect-square shadow-sm">
                <img src={imgUrl(g.image_url)} alt={g.caption} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity"><span className="text-xs uppercase tracking-wide text-white">{g.caption}</span></div>
                {g.type === "before_after" && <Badge className="absolute top-2 left-2 rounded-md bg-primary text-primary-foreground text-[10px]">Before / After</Badge>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <section id="reviews" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <Label>Google Reviews</Label>
            <h2 className="mt-3 text-3xl sm:text-4xl font-head font-black uppercase tracking-tighter">What Customers Say</h2>
            {settings.google_rating && (
              <div className="mt-3 flex items-center gap-2" data-testid="google-rating">
                <div className="flex gap-0.5 text-amber-400">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400" />)}</div>
                <span className="font-head font-black text-xl">{settings.google_rating}</span>
                {settings.google_review_count && <span className="text-slate-500 text-sm">· {settings.google_review_count} Google reviews</span>}
              </div>
            )}
          </div>
          {settings.google_review_url && (
            <a href={settings.google_review_url} target="_blank" rel="noreferrer">
              <Button data-testid="leave-review-btn" className="rounded-md bg-primary text-primary-foreground font-bold uppercase tracking-wider"><Star className="w-4 h-4 mr-2" />Leave Us a Review</Button>
            </a>
          )}
        </div>
        {reviews.length > 0 ? (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex gap-1 text-amber-400">{Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400" />)}</div>
                <p className="mt-4 text-sm text-slate-700">“{r.text}”</p>
                <div className="mt-4 flex items-center justify-between">
                  <p className="font-head font-bold uppercase text-sm">{r.name}</p>
                  {r.date && <span className="text-xs text-slate-400">{r.date}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
            <p className="text-slate-600">Read our latest reviews on Google, and tell us about your experience.</p>
            {settings.google_maps_url && <a href={settings.google_maps_url} target="_blank" rel="noreferrer" className="inline-block mt-4"><Button variant="outline" className="rounded-md border-slate-300">View us on Google</Button></a>}
          </div>
        )}
      </section>

      {/* VISIT US */}
      <section id="contact" className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-10">
          <div>
            <Label>Visit Us</Label>
            <h2 className="mt-3 text-3xl sm:text-4xl font-head font-black uppercase tracking-tighter">{settings.business_name}</h2>
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 text-slate-700"><MapPin className="w-5 h-5 text-primary shrink-0" /> {settings.address}</div>
              <div className="flex items-center gap-3 text-slate-700"><Phone className="w-5 h-5 text-primary shrink-0" /> <a href={telHref(phone)} className="hover:text-primary font-semibold">{phone}</a></div>
              <div className="flex items-center gap-3 text-slate-700"><Check className="w-5 h-5 text-primary shrink-0" /> {settings.email}</div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/book")} className="rounded-md h-12 px-8 bg-primary text-primary-foreground font-bold uppercase tracking-wider">Book Appointment</Button>
              {settings.google_maps_url && <a href={settings.google_maps_url} target="_blank" rel="noreferrer"><Button data-testid="get-directions-btn" variant="outline" className="rounded-md h-12 px-6 border-slate-300"><Navigation className="w-4 h-4 mr-2" />Get Directions</Button></a>}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href={telHref(phone)}><Button variant="outline" className="rounded-md h-11 px-6 border-slate-300"><Phone className="w-4 h-4 mr-2" />Call</Button></a>
              <a href={smsHref(sms)}><Button variant="outline" className="rounded-md h-11 px-6 border-slate-300"><MessageSquare className="w-4 h-4 mr-2" />Text</Button></a>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-hidden min-h-[320px] shadow-sm bg-white">
            <iframe title="WM Window Tint location" src={settings.maps_embed_url} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" />
          </div>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
}
